import React, { useCallback, useEffect, useState } from "react";
import { Colors } from "../../Constants/colors";
import PaginationArrow from "../../images/pagination-arrow.svg";
import { useLocation, useNavigate } from "react-router-dom";

interface Props {
  totalPage: number;
  onPageChange: (pageNo: number) => unknown;
}
enum PAGE_ACTION_TYPE {
  NEXT = "NEXT",
  PREV = "PREV",
}

const getPageFromSearch = (search: string) =>
  parseInt(new URLSearchParams(search).get("page") || "") || 1;

export const Pagination = ({ totalPage, onPageChange }: Props) => {
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pageNumbers, setPageNumbers] = useState<Array<number>>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const page = getPageFromSearch(location.search);
  const safeTotalPage = Number(totalPage);

  const changePageInNavigate = useCallback(
    (nextPage: number, replace = false) => {
      const currentPage = getPageFromSearch(location.search);
      if (nextPage === currentPage) {
        return;
      }

      const updatedSearchParams = new URLSearchParams(location.search);
      updatedSearchParams.set("page", `${nextPage}`);

      const newSearchParamsString = updatedSearchParams.toString();
      const newUrl = `${location.pathname}${
        newSearchParamsString ? "?" + newSearchParamsString : ""
      }`;

      navigate(newUrl, { replace });
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    if (!Number.isFinite(safeTotalPage) || safeTotalPage < 1) {
      setSelectedPage(page);
      return;
    }

    if (page > safeTotalPage) {
      changePageInNavigate(1, true);
      return;
    }

    setSelectedPage(page);
    const windowSize = Math.min(2, safeTotalPage);
    const start = page % 2 === 0 ? page - 1 : page;
    const pages = Array.from(
      { length: windowSize },
      (_, index) => start + index,
    ).filter((pageNumber) => pageNumber >= 1 && pageNumber <= safeTotalPage);

    setPageNumbers(pages);
  }, [page, safeTotalPage, changePageInNavigate]);

  useEffect(() => {
    onPageChange(page);
  }, [page]);

  const handlePageChange = ({
    type,
    index,
  }: {
    type?: PAGE_ACTION_TYPE | null;
    index: number;
  }) => {
    let pageNo = index;

    switch (type) {
      case PAGE_ACTION_TYPE.NEXT:
        pageNo =
          selectedPage === safeTotalPage ? safeTotalPage : selectedPage + 1;
        break;
      case PAGE_ACTION_TYPE.PREV:
        pageNo = selectedPage === 1 ? 1 : selectedPage - 1;
        break;
      default:
        pageNo = index;
    }

    if (pageNo < 1 || pageNo > safeTotalPage || pageNo === page) {
      return;
    }

    changePageInNavigate(pageNo);
    onPageChange(pageNo);
  };

  if (!Number.isFinite(safeTotalPage) || safeTotalPage <= 1) {
    return null;
  }

  return (
    <div className="flex justify-center items-center mt-8">
      <div className="flex w-64 h-14 p-5 justify-center items-center gap-x-2">
        <div
          className="rotate-180"
          style={{ width: "14px", height: "14px", cursor: "pointer" }}
          onClick={() => {
            handlePageChange({
              type: PAGE_ACTION_TYPE.PREV,
              index: selectedPage - 1,
            });
          }}
        >
          <img src={PaginationArrow} alt="next-icon" />
        </div>
        <div className="flex justify-center items-center px-4 gap-x-2">
          {pageNumbers.map((value, index) => {
            const isSelectedIndex = value === selectedPage;
            return (
              <div
                className="w-9 h-9 flex justify-center items-center rounded-md"
                style={{
                  backgroundColor: isSelectedIndex
                    ? Colors.TangerineOrange
                    : "",
                  color: isSelectedIndex ? Colors.White : Colors.DarkGrey,
                  cursor: "pointer",
                  boxShadow: isSelectedIndex
                    ? "rgba(0, 0, 0, 0.24) 0px 3px 8px"
                    : "",
                  textAlign: "center",
                }}
                onClick={() => {
                  handlePageChange({ index: value });
                }}
                key={index}
              >
                {value}
              </div>
            );
          })}
        </div>

        <div
          style={{ width: "14px", height: "14px", cursor: "pointer" }}
          onClick={() => {
            handlePageChange({
              type: PAGE_ACTION_TYPE.NEXT,
              index: selectedPage + 1,
            });
          }}
        >
          <img src={PaginationArrow} alt="next-icon" />
        </div>
      </div>
    </div>
  );
};
